import { createAsyncThunk } from "@reduxjs/toolkit";
import { quizzesApi, paywallsApi } from "@/api";
import { extractApiError } from "@/api/errorStatus";
import type { EditorState } from "../state";

// WEB-2552 (пачка 49, заход Р1): санк смены исходного языка воронки вынесен
// из editorSlice.ts («перенос токен в токен»). Строковое имя санка
// (`editor/changeSourceLanguage`) не менялось. extraReducer
// (`changeSourceLanguage.fulfilled`) остался в фасаде.

/**
 * WEB-1833: смена ИСХОДНОГО языка воронки/экрана оплаты.
 *
 * `language` — это язык СЛУЖЕБНЫХ строк рендера («Далее», «в месяц»,
 * куки-плашка, тексты отказа оплаты); авторские тексты он не переводит.
 * PATCH принимал это поле всегда (UpdateQuizDto.language /
 * UpdatePaywallDto.language), но у конструктора не было двери — единственным
 * путём сменить язык было пересоздать воронку.
 *
 * Ревизия пачки 3, находка 11: про «умолчания новых блоков» тут раньше
 * стояло больше, чем есть на деле. По языку воронки ставятся подписи кнопок
 * и тексты куки-плашки (WEB-785, a46dc915), умолчания СОДЕРЖИМОГО из словаря
 * плеера (WEB-1903: micro_feedback, likert_scale, bmi_scale, подпись
 * спиннера, заголовок блока-лоадера и др.) и, после ревизии пачки 16
 * (находка Ф2), подписи блоков портала — там теперь пусто, а слово даёт
 * фолбэк по языку воронки.
 *
 * ЧТО ОСТАЛОСЬ АНГЛИЙСКИМ (WEB-1907 закрыла вторую половину класса; прежняя
 * редакция этой оговорки перечисляла ещё два десятка настоящих фраз —
 * «Important information», «Only 3 left!», «John D. — Great product!»,
 * «Billed {{price}} monthly…», — все они уехали в словарь плеера). Осталось
 * ровно то, что переводить не решено: НУМЕРАТОРЫ заготовок — «Option 1»,
 * «Option A», «Question 1», «Item 1», «FAQ 1», «Column 1», «Select...»
 * (решение ревизии пачки 3, повторено в границах WEB-1907: без решения
 * владельца не трогаем) — и технические значения полей («apple_pay», «kg»,
 * «PROMO50», маска телефона). Держит это сторож
 * `editorSlice.blockDefaultsNoCyrillic.web1903` двумя концами: ни кириллицы,
 * ни многословного латинского литерала, кроме поимённых нумераторов.
 *
 * Отдельный запрос, в котором НЕТ ни `content`, ни `settings`, ни `logic`:
 * язык не должен ни затирать чужой черновик, ни выгружать наш незаписанный.
 *
 * Ревизия пачки 3, находка 1 (БЛОКЕР): `baseUpdatedAt` шлём и здесь. Прежнее
 * обоснование («запрос содержимого не касается») смотрело только на ТЕЛО
 * запроса и упускало последствие: после успеха снимок сверки берётся из
 * ответа, то есть редактор с устаревшей загрузкой ДОГОНЯЛ штамп записи, ни
 * разу не увидев чужую правку, — и следующее «Сохранить черновик» проходило
 * сверку WEB-1333 п.5 и затирало соседа молча. Со сверкой такой редактор
 * получает 409 STALE_DRAFT здесь же, до записи языка.
 */
export const changeSourceLanguage = createAsyncThunk(
  "editor/changeSourceLanguage",
  async (
    {
      projectId,
      resourceId,
      resourceType,
      language,
    }: {
      projectId: string;
      resourceId: string;
      resourceType: "quiz" | "paywall";
      language: string;
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as { editor: EditorState };
      // WEB-1333 (п.5), зеркало saveQuizDraft/savePaywallDraft: снимок, на
      // котором основан открытый редактор. null (сверка погашена после хода
      // ИИ-чата) и отсутствие поля (старая фикстура) дают undefined — поле не
      // уходит, поведение как раньше.
      const baseUpdatedAt = state.editor.serverUpdatedAt ?? undefined;
      const body = {
        language,
        ...(baseUpdatedAt !== undefined && { baseUpdatedAt }),
      };
      const response =
        resourceType === "quiz"
          ? await quizzesApi.update(projectId, resourceId, body)
          : await paywallsApi.update(projectId, resourceId, body);
      const data = (response.data ?? {}) as Record<string, unknown>;
      return {
        language,
        // WEB-1333 п.5: своя же запись сдвинула штамп записи. Не догнать его —
        // значит подарить следующему сохранению черновика ложный 409
        // STALE_DRAFT. Штампа в ответе нет (старый сервер) → null, то есть
        // сверка гасится до следующей синхронизации (тот же запасной путь,
        // что у invalidateDraftSnapshot, WEB-1831).
        updatedAt:
          typeof data.updatedAt === "string" ? (data.updatedAt as string) : null,
      };
    } catch (error: unknown) {
      // WEB-1966 (форма WEB-1954): отвергаем РАЗОБРАННЫМ отказом целиком —
      // `{ status, message, code, reasonCode }`. Раньше пейлоад собирался
      // здесь руками, и `reasonCode` (ПРИЧИНА денежного отказа 402) терялся
      // ещё в слайсе: экран переключателя языка не мог сказать, что именно
      // упёрлось, сколько бы он ни звал реестр. Запасная фраза с экрана тоже
      // убрана — её место на экране (второй аргумент localizeError), а не в
      // слайсе, который про язык кабинета ничего не знает.
      return rejectWithValue(extractApiError(error));
    }
  },
);
